import React, { useCallback, useState } from 'react'
import Image from 'next/image'
import cx from 'classnames'
import PropTypes from 'prop-types'

import Icon from '@/components/common/Icon'
import Text from '@/components/common/Text'

import styles from './Testimonials.module.scss'

const avatarImages = ['/home2/trust1.webp', '/home2/trust2.webp', '/home2/trust3.webp']
const bottomLogos = [
  { src: '/home2/techcrunch.webp', alt: 'TechCrunch', width: 130 },
  { src: '/home2/wired.webp', alt: 'WIRED', width: 100 },
  { src: '/home2/forbes.png', alt: 'Forbes', width: 130 },
  { src: '/home2/entrepreneur.png', alt: 'Entrepreneur', width: 130 },
  { src: '/home2/inc.png', alt: 'Inc', width: 40 },
]
const starIndexes = [1, 2, 3, 4, 5]

export default function Testimonials({ t }) {
  const [startIndex, setStartIndex] = useState(0)
  const total = t.items.length
  const visibleCount = 3

  const canPrev = startIndex > 0
  const canNext = startIndex + visibleCount < total

  const goPrev = useCallback(() => {
    if (canPrev) {
      setStartIndex((i) => i - 1)
    }
  }, [canPrev])

  const goNext = useCallback(() => {
    if (canNext) {
      setStartIndex((i) => i + 1)
    }
  }, [canNext])

  const visibleItems = t.items

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        <Text.H2 className={styles.title}>{t.title}</Text.H2>
        <Text.P className={styles.subtitle}>{t.subtitle}</Text.P>

        <div className={styles.carouselWrap}>
          <button
            aria-label="Previous"
            className={cx(styles.arrowBtn, styles.arrowLeft, { [styles.arrowDisabled]: !canPrev })}
            onClick={goPrev}
          >
            <Icon fill="white" name="chevron-left" />
          </button>

          <div className={styles.carouselViewport}>
            <div className={styles.cards} style={{ '--slide-index': startIndex }}>
              {visibleItems.map((item) => (
                <div key={item.name} className={styles.card}>
                  <div className={styles.cardTop}>
                    <div className={styles.logoAvatar}>
                      <Image alt={item.name} height={40} src={item.logo} width={40} />
                    </div>
                    <div className={styles.authorInfo}>
                      <Text className={styles.authorName}>{item.name}</Text>
                      <Text className={styles.authorRole}>{item.role}</Text>
                    </div>
                  </div>
                  <div className={styles.starsRow}>
                    {starIndexes.map((star) => (
                      <Image
                        key={`testimonial-star-${item.name}-${star}`}
                        alt="star"
                        height={13}
                        src="/home2/star-icon.svg"
                        width={14}
                      />
                    ))}
                  </div>
                  <div className={styles.quoteBody}>
                    <Image
                      alt=""
                      className={styles.quoteOpen}
                      height={15}
                      src="/home2/quote-icon.svg"
                      width={15}
                    />
                    <Text.P className={styles.quoteText}>{item.quote}</Text.P>
                    <Image
                      alt=""
                      className={styles.quoteClose}
                      height={15}
                      src="/home2/quote-icon.svg"
                      width={15}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button
            aria-label="Next"
            className={cx(styles.arrowBtn, styles.arrowRight, { [styles.arrowDisabled]: !canNext })}
            onClick={goNext}
          >
            <Icon fill="white" name="chevron-right" />
          </button>
        </div>

        <div className={styles.bottomRow}>
          <div className={styles.bottomLeft}>
            <div className={styles.stackedAvatars}>
              {avatarImages.map((avatar, index) => (
                <div key={avatar} className={styles.avatarCircle} style={{ width: 36, height: 36 }}>
                  <Image
                    alt={`Neat User Trust ${index + 1}`}
                    className={styles.testimonialImg}
                    height={36}
                    src={avatar}
                    width={36}
                  />
                </div>
              ))}
            </div>
            <div className={styles.bottomStars}>
              {starIndexes.map((star) => (
                <Image
                  key={`bottom-star-${star}`}
                  alt="star"
                  height={13}
                  src="/home2/star-icon.svg"
                  width={14}
                />
              ))}
            </div>
            <Text className={styles.reviewCount}>{t.reviewCount}</Text>
          </div>
          <div className={styles.bottomRight}>
            {bottomLogos.map((logo) => (
              <div key={logo.src} className={styles.bottomLogoItem}>
                <Image
                  alt={logo.alt}
                  height={28}
                  src={logo.src}
                  style={{ objectFit: 'contain', filter: 'brightness(0) invert(1)' }}
                  width={logo.width}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

Testimonials.propTypes = {
  t: PropTypes.shape({
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string.isRequired,
    reviewCount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        role: PropTypes.string.isRequired,
        quote: PropTypes.string.isRequired,
        logo: PropTypes.string,
      }),
    ).isRequired,
  }).isRequired,
}
